/* ============================================================================
   Quick view (2026 design). Loaded by snippets/kc-qv-dialog.liquid when
   Theme settings > Redesign 2026 > Quick view is on.

   From 990px, the product card's "Quick view" pill (and the round
   magnifying-glass button, snippets/kc-qv-button.liquid, on Meet the
   characters and the header's polaroid piles) opens the product in a
   <dialog> on dark glass instead of going to its page. The pill is a link
   to the product, so phones, ctrl/cmd-clicks, the middle button and pages
   without this script still go to the page. The product comes from
   sections/kc-quick-view.liquid, fetched by file name from the product's
   URL: a pointer on the pill (or the keyboard focus) fetches it before the
   click, and each product is fetched once per page. The stylesheet loads
   the first time a pointer comes near a card. If a product can't be
   fetched, the click goes to its page after all.

   <kc-quick-view>  the product inside the pop-up: photos (thumbnails,
                    previous / next, arrow keys, swipes), quantity, option
                    pills and Add to cart. Adding keeps the pop-up open: the
                    cart drawer and the bag bubbles refresh behind it
                    without opening, the button says "Added", and a note
                    under it carries the bag's free-shipping line, read from
                    the refreshed drawer. "View bag" closes the pop-up and
                    opens the drawer.
   ============================================================================ */
(() => {
  if (window.kcQuickView) return;
  window.kcQuickView = true;

  const money = (cents) => {
    let out;
    try {
      out = theme.Currency.formatMoney(cents, theme.shopSettings.moneyFormat);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
    // the shop's money format may carry markup or entities; keep the text
    return /[<&]/.test(out) ? new DOMParser().parseFromString(out, 'text/html').body.textContent : out;
  };

  const strings = () => (window.theme && theme.variantStrings) || {
    addToCart: 'Add to cart',
    soldOut: 'Sold out',
    unavailable: 'Unavailable'
  };

  // disable a button, handing the focus on if it had it (a disabled button
  // drops the focus out of the pop-up)
  const setDisabled = (btn, off, next) => {
    if (!btn) return;
    if (off && document.activeElement === btn && next) next.focus();
    btn.disabled = off;
  };

  /* ------------------------------------------------------------------------
     <kc-quick-view>
     ------------------------------------------------------------------------ */
  class KcQuickView extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.form = this.querySelector('[data-kc-qv-form]');
      this.idInput = this.form && this.form.querySelector('input[name="id"]');
      this.qty = this.querySelector('[data-kc-qv-qty-input]');
      this.atc = this.querySelector('[data-kc-qv-atc]');
      this.atcLabel = this.querySelector('[data-kc-qv-atc-label]');
      this.error = this.querySelector('[data-kc-qv-error]');
      this.price = this.querySelector('[data-kc-qv-price]');
      this.addedBox = this.querySelector('[data-kc-qv-added]');
      this.live = this.querySelector('[data-kc-qv-live]');
      this.more = this.querySelector('[data-kc-qv-more]');
      this.big = this.querySelector('[data-kc-qv-big]');
      this.cap = this.querySelector('[data-kc-qv-cap]');
      this.thumbs = Array.from(this.querySelectorAll('[data-kc-qv-th]'));
      this.index = Number(this.dataset.start) || 0;

      const json = this.querySelector('[data-kc-qv-variants]');
      try {
        this.variants = json ? JSON.parse(json.textContent) : [];
      } catch (e) {
        this.variants = [];
      }
      this.variant = this.variants.find((v) => this.idInput && String(v.id) === this.idInput.value)
        || this.variants[0] || null;

      this.bindPhotos();
      this.bindQty();
      this.bindOptions();
      this.bindForm();
      this.bindDesc();
    }

    /* --- photos ----------------------------------------------------------- */
    bindPhotos() {
      this.thumbs.forEach((th) => th.addEventListener('click', () => this.show(Number(th.dataset.kcQvTh))));
      this.querySelectorAll('[data-kc-qv-step]').forEach((btn) => {
        btn.addEventListener('click', () => this.step(Number(btn.dataset.kcQvStep)));
      });

      // a swipe across the photo turns it (touch screens)
      const photo = this.querySelector('[data-kc-qv-photo]');
      if (!photo || this.thumbs.length < 2) return;
      let start = null;
      photo.addEventListener('pointerdown', (e) => {
        start = e.pointerType === 'mouse' ? null : { x: e.clientX, y: e.clientY };
      });
      photo.addEventListener('pointerup', (e) => {
        if (!start) return;
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        start = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.step(dx < 0 ? 1 : -1);
      });
      photo.addEventListener('pointercancel', () => {
        start = null;
      });
    }

    step(by) {
      if (this.thumbs.length > 1) this.show(this.index + by);
    }

    show(i) {
      const count = this.thumbs.length;
      if (!count || !this.big) return;
      this.index = ((i % count) + count) % count;
      const th = this.thumbs[this.index];
      this.big.srcset = th.dataset.srcset || '';
      this.big.src = th.dataset.src;
      this.big.alt = th.dataset.alt || '';
      if (this.cap) {
        this.cap.textContent = (this.dataset.caption || '[n] / [count]')
          .replace('[n]', this.index + 1)
          .replace('[count]', count);
      }
      this.thumbs.forEach((t, j) => t.setAttribute('aria-pressed', j === this.index ? 'true' : 'false'));
      // warm the next photo
      const next = this.thumbs[(this.index + 1) % count];
      if (next !== th) {
        const img = new Image();
        img.sizes = this.big.sizes;
        img.srcset = next.dataset.srcset || '';
        img.src = next.dataset.src;
      }
    }

    /* --- quantity --------------------------------------------------------- */
    bindQty() {
      if (!this.qty) return;
      this.steps = Array.from(this.querySelectorAll('[data-kc-qv-qty]'));
      this.steps.forEach((btn) => {
        btn.addEventListener('click', () => {
          const step = Number(this.qty.step) || 1;
          this.setQty((Number(this.qty.value) || 0) + step * Number(btn.dataset.kcQvQty));
        });
      });
      this.qty.addEventListener('change', () => this.setQty(Number(this.qty.value)));
      this.setQty(Number(this.qty.value), true);
    }

    setQty(n, quiet) {
      const min = Number(this.qty.min) || 1;
      const max = this.qty.max ? Number(this.qty.max) : Infinity;
      let v = Number.isFinite(n) ? Math.round(n) : min;
      v = Math.max(min, Math.min(max, v));
      this.qty.value = v;
      const [less, more] = this.steps;
      setDisabled(less, v <= min, more);
      setDisabled(more, v >= max, less);
      if (!quiet) this.clearAdded();
    }

    /* --- options ---------------------------------------------------------- */
    bindOptions() {
      this.optionInputs = Array.from(this.querySelectorAll('[data-kc-qv-option]'));
      if (!this.optionInputs.length || !this.variants.length) return;
      this.optionInputs.forEach((input) => input.addEventListener('change', () => this.onOptionChange()));
      this.markSoldOut(this.picked());
    }

    picked() {
      const picked = [];
      this.optionInputs.forEach((input) => {
        if (input.checked) picked[Number(input.dataset.kcQvOption)] = input.value;
      });
      return picked;
    }

    // cross out values that are sold out with the other picks
    markSoldOut(picked) {
      this.optionInputs.forEach((input) => {
        const i = Number(input.dataset.kcQvOption);
        const ok = this.variants.some((v) => v.available && v.options[i] === input.value
          && v.options.every((value, k) => k === i || value === picked[k]));
        input.closest('.kc-qv__val').classList.toggle('is-out', !ok);
      });
    }

    onOptionChange() {
      const picked = this.picked();
      const match = this.variants.find((v) => v.options.every((value, i) => value === picked[i]));
      const s = strings();
      this.variant = match || null;
      if (this.idInput) this.idInput.value = match ? match.id : '';
      this.clearError();
      this.clearAdded();

      if (!match) {
        this.atc.disabled = true;
        this.atcLabel.textContent = s.unavailable;
      } else {
        this.atc.disabled = !match.available;
        this.atcLabel.textContent = match.available ? s.addToCart : s.soldOut;
        this.renderPrice(match);
        this.showVariantPhoto(match);
        if (this.more && this.dataset.url) {
          const url = new URL(this.dataset.url, window.location.href);
          url.searchParams.set('variant', match.id);
          this.more.href = url.pathname + url.search;
        }
      }
      this.markSoldOut(picked);
    }

    renderPrice(v) {
      if (!this.price) return;
      const parts = [];
      if (v.compare_at_price > v.price) {
        const was = document.createElement('s');
        const regular = document.createElement('span');
        regular.className = 'kc-vh';
        regular.textContent = this.price.dataset.labelRegular || 'Regular price';
        was.append(regular, money(v.compare_at_price));
        const sale = document.createElement('span');
        sale.className = 'kc-vh';
        sale.textContent = this.price.dataset.labelSale || 'Sale price';
        parts.push(was, ' ', sale);
      }
      parts.push(money(v.price));
      this.price.replaceChildren(...parts);
    }

    showVariantPhoto(v) {
      const id = v.featured_media && v.featured_media.id;
      if (!id) return;
      const k = this.thumbs.findIndex((t) => t.dataset.mediaId === String(id));
      if (k >= 0) this.show(k);
    }

    /* --- add to cart ------------------------------------------------------ */
    bindForm() {
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => {
        const cart = document.querySelector('mini-cart');
        const drawer = window.theme && theme.shopSettings && theme.shopSettings.cartDrawer;
        // no cart drawer, or the cart page itself: let the form post and go to the cart
        if (!cart || !drawer || document.body.classList.contains('template-cart')) return;
        e.preventDefault();
        if (this.atc.disabled || this.atc.classList.contains('is-loading')) return;
        this.add(cart);
      });

      const bag = this.querySelector('[data-kc-qv-bag]');
      if (bag) {
        bag.addEventListener('click', (e) => {
          const cart = document.querySelector('mini-cart');
          if (!cart || typeof cart.open !== 'function' || document.body.classList.contains('template-cart')) return;
          e.preventDefault();
          this.dispatchEvent(new CustomEvent('kc-qv:leave', { bubbles: true }));
          cart.open();
        });
      }
    }

    async add(cart) {
      this.clearError();
      this.atc.classList.add('is-loading');
      this.atc.setAttribute('aria-busy', 'true');

      const body = new FormData(this.form);
      // the option pills only pick the variant
      Array.from(body.keys()).forEach((key) => {
        if (key.indexOf('kc-qv-opt-') === 0) body.delete(key);
      });
      const sections = typeof cart.getSectionsToRender === 'function' ? cart.getSectionsToRender() : [];
      body.append('sections', sections.map((s) => s.id));
      body.append('sections_url', window.location.pathname);

      try {
        const res = await fetch(theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
          body
        });
        const data = await res.json();
        if (data.status) {
          this.showError(data.description || data.message);
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartError, { source: 'kc-quick-view', productVariantId: body.get('id'), errors: data.errors || data.description, message: data.message });
          }
          return;
        }
        this.refreshBag(cart, sections, data);
        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'kc-quick-view', productVariantId: body.get('id'), cartData: data });
        }
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data } }));
        this.showAdded(data);
      } catch (err) {
        console.error(err);
        this.showError(window.theme && theme.cartStrings ? theme.cartStrings.error : 'Something went wrong. Please try again.');
      } finally {
        this.atc.classList.remove('is-loading');
        this.atc.removeAttribute('aria-busy');
      }
    }

    // the cart drawer and the bag bubbles, like the theme's cart drawer
    // does after an add, but without opening the drawer over the pop-up
    refreshBag(cart, sections, data) {
      const parser = new DOMParser();
      sections.forEach((s) => {
        const el = document.getElementById(s.id);
        const html = data.sections && data.sections[s.id];
        if (!el || typeof html !== 'string') return;
        const part = parser.parseFromString(html, 'text/html').querySelector(s.selector || '.shopify-section');
        if (part) el.innerHTML = part.innerHTML;
      });
      cart.classList.remove('is-empty');
    }

    showAdded(data) {
      this.atc.classList.add('is-added');
      if (!this.addedBox) return;

      // the free-shipping line and bar, as the refreshed bag shows them
      const html = data.sections && data.sections['mini-cart'];
      const meter = typeof html === 'string'
        ? new DOMParser().parseFromString(html, 'text/html').querySelector('.kc-meter')
        : null;
      const line = meter && meter.querySelector('p');
      const fill = meter && meter.querySelector('.kc-meter__bar i');
      const text = this.querySelector('[data-kc-qv-meter]');
      const bar = this.querySelector('[data-kc-qv-bar]');
      if (text) {
        text.hidden = !line;
        text.replaceChildren(...(line ? Array.from(line.childNodes, (n) => document.importNode(n, true)) : []));
      }
      if (bar) {
        bar.hidden = !fill;
        if (fill) {
          bar.firstElementChild.style.width = fill.style.width;
          bar.classList.toggle('is-free', meter.classList.contains('is-free'));
        }
      }
      this.addedBox.hidden = false;
      this.addedBox.scrollIntoView({ block: 'nearest' });

      if (this.live) {
        const note = this.addedBox.querySelector('.kc-qv__added-t span');
        const message = [note ? note.textContent : '', line ? line.textContent : ''].join(' ').trim();
        this.live.textContent = '';
        requestAnimationFrame(() => {
          this.live.textContent = message;
        });
      }
    }

    clearAdded() {
      if (this.atc) this.atc.classList.remove('is-added');
      if (this.addedBox) this.addedBox.hidden = true;
    }

    showError(message) {
      if (!this.error || !message) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    clearError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.error.textContent = '';
    }

    /* --- the description --------------------------------------------------
       It takes the card's leftover height and scrolls inside it; while
       there's more below, the text fades out at the bottom. A scrolling
       description can take the focus so it scrolls with the arrow keys. */
    bindDesc() {
      const desc = this.querySelector('[data-kc-qv-desc]');
      if (!desc) return;
      const update = () => {
        const scrolls = desc.scrollHeight > desc.clientHeight + 1;
        desc.classList.toggle('is-scroll', scrolls);
        desc.classList.toggle('is-end', desc.scrollTop + desc.clientHeight >= desc.scrollHeight - 2);
        if (scrolls) desc.tabIndex = 0;
        else desc.removeAttribute('tabindex');
      };
      update();
      desc.addEventListener('scroll', update, { passive: true });
      desc.addEventListener('load', update, true);
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(desc);
    }
  }

  if (!customElements.get('kc-quick-view')) customElements.define('kc-quick-view', KcQuickView);

  /* ------------------------------------------------------------------------
     The pop-up
     ------------------------------------------------------------------------ */
  const dialog = document.getElementById('kc-qv');
  if (!dialog || typeof dialog.showModal !== 'function') return;

  const wide = window.matchMedia('(min-width: 990px)');
  const inner = dialog.querySelector('[data-kc-qv-in]');
  const waiting = inner.innerHTML;
  const pages = new Map();
  let trigger = null;
  let turn = 0;
  let giveFocusBack = true;
  let downOnGlass = false;

  // the stylesheet, once
  let css = null;
  const loadCss = () => {
    if (!css) {
      css = new Promise((resolve) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = dialog.dataset.css;
        link.addEventListener('load', resolve);
        link.addEventListener('error', resolve);
        document.head.append(link);
      });
    }
    return css;
  };

  // each product's HTML, once per page
  const fetchProduct = (href) => {
    const url = new URL(href, window.location.href);
    url.hash = '';
    url.searchParams.set('section_id', 'kc-quick-view');
    const key = url.toString();
    if (!pages.has(key)) {
      const page = fetch(key).then((res) => {
        if (!res.ok) throw new Error(`Quick view: ${res.status}`);
        return res.text();
      });
      page.catch(() => pages.delete(key));
      pages.set(key, page);
    }
    return pages.get(key);
  };

  const parse = (html) => {
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    return tpl.content.querySelector('kc-quick-view');
  };

  /* Before the product goes in, and on resize: the photo as big as the
     window allows (560px at most) with every thumbnail showing, none
     scrolling, like the product page's photo viewer. The sums follow
     kawaii-quick-view.css: the pop-up's padding (40 a side across, 64 top
     and bottom), the polaroid's frame (28 across, 60 down), the card
     (420px, 380px under 1200px), and a thumbnail plus 8px of gap or
     padding per column or row. */

  // C4: the thumbnails in columns on the left (two at least; past that
  // they get smaller), the card overlapping the polaroid by 36px. A column
  // takes the thumbnail + 8, the grid's padding and its gap 36 more.
  const fitBeside = (count, vw, vh, card) => {
    const byHeight = Math.min(560, vh - 188);
    const room = (side) => Math.min(byHeight, vw - 80 - 28 - (card - 36) - side);
    if (count < 2) return { cols: 2, th: 52, ph: room(0) };

    let pick = null;
    search: for (const th of [52, 44, 36]) {
      for (let cols = 2; cols <= 6; cols++) {
        const ph = room(cols * (th + 8) + 36);
        if (ph < 300) break;
        pick = { cols, th, ph };
        // the grid stands beside the polaroid (photo + 60) over the arrows (46 + 14)
        if (cols * Math.max(1, Math.floor(ph / (th + 8))) >= count) break search;
      }
    }
    return pick || { cols: 2, th: 36, ph: room(2 * 44 + 36) };
  };

  // C1: the card 44px to the right of the photos' column; the thumbnails
  // in rows 16px under the photo, between the arrows (46 + 12 a side, so
  // 116px of a row). The rows come off the photo's height, so the photo
  // steps down until photo and rows fit. A row may run up to 60px past
  // the polaroid on each side when that saves a row, and the rows are
  // evened out (7 + 7, not 9 + 5). Each thumbnail size is tried; the
  // biggest wins unless the photo would come out over a tenth smaller
  // than with the smallest.
  const fitBelow = (count, vw, vh, card) => {
    const roomW = vw - 80 - 44 - card;
    const roomH = vh - 128;
    const maxPh = Math.min(560, roomW - 28, roomH - 60);
    if (count < 2) return { th: 52, ph: maxPh };

    const tries = [52, 44, 36].map((th) => {
      const unit = th + 8;
      let last = null;
      for (let ph = maxPh; ph >= 280; ph -= 4) {
        const perRow = Math.max(1, Math.floor((Math.min(roomW, ph + 148) - 116) / unit));
        const rows = Math.ceil(count / perRow);
        const row = Math.ceil(count / rows) * unit;
        last = { th, ph, row, gal: Math.max(ph + 28, row + 116) };
        if (ph + 76 + Math.max(46, rows * unit) <= roomH) return last;
      }
      // a very short window: the smallest photo, and the pop-up scrolls
      return last || { th, ph: maxPh };
    });
    const smallest = tries[2].ph;
    return tries.find((t) => t.ph >= smallest * 0.9) || tries[2];
  };

  const fit = (count) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const card = vw >= 1200 ? 420 : 380;
    const pick = dialog.classList.contains('kc-qv--c1')
      ? fitBelow(count, vw, vh, card)
      : fitBeside(count, vw, vh, card);

    const style = dialog.style;
    style.setProperty('--qv-ph', `${Math.max(240, Math.floor(pick.ph))}px`);
    style.setProperty('--qv-card', `${card}px`);
    style.setProperty('--qv-cols', pick.cols || 2);
    style.setProperty('--qv-th', `${pick.th}px`);
    // C1 only: the photos' column and the thumbnail rows' widths
    if (pick.gal) style.setProperty('--qv-gal', `${pick.gal}px`);
    else style.removeProperty('--qv-gal');
    if (pick.row) style.setProperty('--qv-row', `${pick.row}px`);
    else style.removeProperty('--qv-row');
  };

  const open = async (link) => {
    const mine = ++turn;
    trigger = link;
    giveFocusBack = true;
    const page = fetchProduct(link.href);
    page.catch(() => {});

    link.setAttribute('aria-busy', 'true');
    await loadCss();
    link.removeAttribute('aria-busy');
    if (mine !== turn) return;

    inner.innerHTML = waiting;
    dialog.classList.add('is-waiting');
    if (!dialog.open) dialog.showModal();

    let view = null;
    try {
      view = parse(await page);
    } catch (e) {
      view = null;
    }
    if (mine !== turn || !dialog.open) return;
    if (!view) {
      // couldn't load it here: go to the product page instead
      window.location.href = link.href;
      return;
    }
    fit(Number(view.dataset.count) || 0);
    inner.replaceChildren(view);
    dialog.classList.remove('is-waiting');
  };

  dialog.addEventListener('close', () => {
    turn++;
    inner.innerHTML = waiting;
    dialog.classList.remove('is-waiting');
    const back = trigger;
    trigger = null;
    // browsers that don't hand the focus back to the pill themselves
    const lost = !document.activeElement || document.activeElement === document.body || dialog.contains(document.activeElement);
    if (giveFocusBack && lost && back && back.isConnected) back.focus({ preventScroll: true });
  });

  // "View bag": close without handing the focus back, the drawer takes it
  dialog.addEventListener('kc-qv:leave', () => {
    giveFocusBack = false;
    dialog.close();
  });

  // the glass around the product closes it (only when the press started
  // there too, so selecting text in the card never does)
  const isGlass = (t) => t === dialog || t === inner
    || (t && (t.localName === 'kc-quick-view' || t.classList.contains('kc-qv__gal')));
  dialog.addEventListener('pointerdown', (e) => {
    downOnGlass = isGlass(e.target);
  });
  dialog.addEventListener('click', (e) => {
    if (e.target.closest('[data-kc-qv-close]') || (downOnGlass && isGlass(e.target))) dialog.close();
  });

  // left / right turn the photos, except in the quantity field and the option pills
  dialog.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.target.closest('input, select, textarea, [contenteditable]')) return;
    const view = inner.querySelector('kc-quick-view');
    if (!view || typeof view.step !== 'function') return;
    e.preventDefault();
    view.step(e.key === 'ArrowLeft' ? -1 : 1);
  });

  window.addEventListener('resize', () => {
    if (!dialog.open) return;
    const view = inner.querySelector('kc-quick-view');
    if (view) fit(Number(view.dataset.count) || 0);
  });

  /* --- the pills ---------------------------------------------------------- */
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const link = e.target.closest && e.target.closest('a[data-kc-qv]');
    if (!link || !wide.matches) return;
    e.preventDefault();
    open(link);
  });

  // a pointer on a card (or another photo with a quick-view button, marked
  // data-kc-qv-zone) loads the stylesheet; on the button, the product too
  const warm = (e) => {
    if (!wide.matches || !e.target.closest || !e.target.closest('.kc-card--qv, [data-kc-qv-zone]')) return;
    loadCss();
    const link = e.target.closest('a[data-kc-qv]');
    if (link) fetchProduct(link.href).catch(() => {});
  };
  document.addEventListener('pointerover', warm, { passive: true });
  document.addEventListener('focusin', warm);
})();
